import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import Seo, { faqLd, serviceLd } from '../../components/Seo';
import Reveal from '../../components/Reveal';
import {
  CheckItem, Faq, IconCards, Intro, ProductHero, QuoteSection, SectionTitle, Steps, TrustStrip,
} from '../../components/product/ProductKit';
import { TEAMS_PHONE as D } from '../../data/teamsPhone';
import { useSiteContent, withProductContent } from '../../content/siteContent';
import { vatNote } from '../../config/vat';

/** Plan cards with two toggles: Teams calling vs Teams + Microsoft 365, and monthly vs annual billing. */
const Plans = () => {
  const [tab, setTab] = useState(D.plans.tabs[0]);
  const [annual, setAnnual] = useState(false);
  const toggle = (label, options, value, onChange) => (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-full border border-gray-200 bg-white p-1">
      {options.map(([text, v]) => (
        <button
          key={text}
          type="button"
          role="radio"
          aria-checked={value === v}
          onClick={() => onChange(v)}
          className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${value === v ? 'bg-[#0a1f2e] text-white' : 'text-gray-600 hover:text-[#0a1f2e]'}`}
        >
          {text}
        </button>
      ))}
    </div>
  );
  return (
    <>
      <div className="mb-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
        {toggle('Plan type', D.plans.tabs.map((t) => [t, t]), tab, setTab)}
        {toggle('Billing period', [['Monthly', false], ['Annual (save ~20%)', true]], annual, setAnnual)}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {D.plans[tab].map((p, i) => (
          <Reveal
            key={`${tab}-${p.name}`}
            delay={i * 80}
            className={`relative flex flex-col rounded-3xl p-8 ${p.highlight ? 'bg-[#0a1f2e] text-white shadow-[0_30px_80px_-30px_rgba(10,31,46,0.6)] lg:-my-4 lg:py-12' : 'border border-gray-200 bg-white'}`}
          >
            {p.highlight && <span className="absolute -top-3 left-8 rounded-full bg-[#c8ff00] px-3 py-1 text-xs font-bold text-[#0a1f2e]">Most popular</span>}
            <h3 className="font-display text-2xl font-semibold">{p.name}</h3>
            <p className={`mt-1 text-sm ${p.highlight ? 'text-gray-300' : 'text-gray-600'}`}>{p.tagline}</p>
            <div className="mt-6 flex items-end gap-1">
              <span className="font-display text-5xl font-semibold">£{annual ? p.annual : p.monthly}</span>
              <span className={`mb-1.5 text-sm ${p.highlight ? 'text-gray-300' : 'text-gray-500'}`}>/user/month</span>
            </div>
            <p className={`mt-1 text-xs ${p.highlight ? 'text-gray-400' : 'text-gray-500'}`}>{annual ? 'billed annually' : 'billed monthly'}, {vatNote(true)}</p>
            <ul className={`mt-8 flex-1 space-y-3 text-sm ${p.highlight ? 'text-gray-200' : 'text-gray-700'}`}>
              {p.features.map((f) => <CheckItem key={f}>{f}</CheckItem>)}
            </ul>
            <a
              href="#quote"
              className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold transition active:scale-[0.98] ${p.highlight ? 'bg-[#c8ff00] text-[#0a1f2e] hover:brightness-95' : 'border border-[#0a1f2e]/25 text-[#0a1f2e] hover:bg-[#0a1f2e] hover:text-white'}`}
            >
              Get started <ArrowRight className="h-4 w-4" />
            </a>
          </Reveal>
        ))}
      </div>
      <p className="mx-auto mt-10 max-w-3xl text-center text-sm text-gray-500">{D.plans.note}</p>
    </>
  );
};

const TeamsPhone = () => {
  const P = withProductContent(useSiteContent('teams-phone'), D, { trust: false });
  return (
  <div className="min-h-screen bg-white text-[#0a1f2e]">
    <Seo
        title="Microsoft Teams Phone System & Direct Routing"
        description="Turn Microsoft Teams into a full business phone system with VOIP2 Direct Routing. Call any number from Teams, keep your numbers and add queues and IVRs."
        path="/products/teams-phone-system"
        breadcrumbs={[{ name: 'Microsoft Teams Phone System', path: '/products/teams-phone-system' }]}
        jsonLd={[serviceLd({ name: 'Microsoft Teams Phone System', description: "Turn Microsoft Teams into a full business phone system with VOIP2 Direct Routing. Call any number from Teams, keep your numbers and add queues and IVRs.", path: '/products/teams-phone-system' }), faqLd(D.faqs)]}
      />

    <ProductHero
      {...P.hero}
      secondary={{ label: 'See the plans', href: '#plans' }}
      media={(
        <>
          <div className="absolute inset-x-8 bottom-6 top-16 -z-10 rounded-[3rem] bg-[radial-gradient(ellipse_at_center,rgba(200,255,0,0.3),transparent_65%)]" />
          <img src={D.hero.image} alt="A laptop and a mobile phone showing a call in a team chat app, with a headset" className="w-full" />
        </>
      )}
    />

    <TrustStrip items={D.trust} />

    <Intro {...P.intro} />

    <section className="bg-[#f5f7f9] px-4 py-24 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <SectionTitle title={D.features.title} sub={D.features.sub} />
        <IconCards items={D.features.items} />
      </div>
    </section>

    <section id="plans" className="scroll-mt-20 px-4 py-24 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <SectionTitle title="Simple per-user pricing" sub="Add calling to the Teams licences you already have, or let us supply Microsoft 365 as well." center />
        <Plans />
      </div>
    </section>

    <section className="bg-[#f5f7f9] px-4 py-24 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <SectionTitle title="Live in three steps" />
        <Steps steps={D.steps} />
      </div>
    </section>

    <Faq faqs={D.faqs} />

    <QuoteSection
      title="Add calling to Microsoft Teams"
      sub="Tell us how many users you have and which numbers you want to keep. We will send a quote within one working day."
      service="Microsoft Teams Phone"
      contextLabel="How many users?"
      contextOptions={D.userOptions}
      related={D.related}
    />
  </div>
  );
};

export default TeamsPhone;
